import {
    formatCurrency,
    getLocaleNumberFormatNoDecimals,
    getLocaleNumberFormatTwoDecimals,
} from '@repo/i18n';
import type { Currency, Translations } from '@repo/types';
import { capitalize } from '@repo/common-utils/TextUtils';
import type { PriceSummary } from '@repo/widget-utils/price-helper';
import type { SummaryCartItem } from 'src/components/domain/summary/summary-mapping';
import type {
    ReceiptItemGroup,
    ReceiptLineItem,
    ReceiptTicketLine,
    WidgetReceiptData,
} from './print-receipt.types';

type ActivityReceiptInput = {
    bookingRef: string | null;
    summaryCartItemsByType: Record<string, SummaryCartItem[]>;
    priceSummary: PriceSummary;
    appliedPromoCode: string | null;
    promoCodeDiscountAmount: number;
    giftCardDiscountAmount: number;
    // Native member-card coverage (whole NOK). memberCardTotal, when set, overrides the grand total.
    memberCardDiscountAmount: number;
    memberCardTotal: number | null;
    logoUrl: string | null;
    locale: string;
    currency: Currency;
    t: Translations;
};

export function buildActivityReceiptData(input: ActivityReceiptInput): WidgetReceiptData {
    const { priceSummary, locale, currency, t } = input;

    const itemGroups: ReceiptItemGroup[] = Object.entries(input.summaryCartItemsByType)
        .filter(([, items]) => items.length > 0)
        .map(([groupName, items]) => ({
            groupName: capitalize(groupName),
            items: items.map(mapSummaryCartItemToLineItem),
        }));

    const vatLines = Object.keys(priceSummary.vatAmountInfo)
        // Sort VAT rates descending, matching SummaryVat.tsx display order
        .sort((a, b) => parseFloat(b) - parseFloat(a))
        .filter((key) => priceSummary.vatAmountInfo[key] > 0)
        .map((key) => ({
            label: `${t.vat} ${key}%`,
            amount: getLocaleNumberFormatTwoDecimals(locale, priceSummary.vatAmountInfo[key]),
        }));

    const hasPromo = input.promoCodeDiscountAmount > 0;
    const hasGiftCard = input.giftCardDiscountAmount > 0;
    const hasMemberCard = input.memberCardDiscountAmount > 0;

    return {
        logoUrl: input.logoUrl,
        heading: capitalize(t.summary),
        bookingRef: input.bookingRef,
        bookingRefLabel: capitalize(t.your_booking_reference_is),
        itemGroups,
        exVatLabel: `${capitalize(t.excl)} ${t.vat}`,
        exVatAmount: getLocaleNumberFormatTwoDecimals(locale, priceSummary.totalPriceExVat),
        vatLines,
        promoLabel: hasPromo ? (input.appliedPromoCode ?? capitalize(t.discount)) : null,
        promoAmount: hasPromo
            ? `-${getLocaleNumberFormatNoDecimals(locale, input.promoCodeDiscountAmount)}`
            : null,
        giftCardLabel: hasGiftCard ? capitalize(t.gift_card) : null,
        giftCardAmount: hasGiftCard
            ? `-${getLocaleNumberFormatNoDecimals(locale, input.giftCardDiscountAmount)}`
            : null,
        memberCardLabel: hasMemberCard ? capitalize(t.membershipCard) : null,
        memberCardAmount: hasMemberCard
            ? `-${getLocaleNumberFormatNoDecimals(locale, input.memberCardDiscountAmount)}`
            : null,
        totalLabel: t.total.toUpperCase(),
        totalAmount: formatCurrency(
            locale,
            currency,
            input.memberCardTotal ?? priceSummary.totalPrice,
            0,
            2,
        ),
        footerNote: capitalize(t.booking_details_in_email),
    };
}

function mapSummaryCartItemToLineItem(item: SummaryCartItem): ReceiptLineItem {
    const tickets: ReceiptTicketLine[] = item.priceLines
        .filter((line) => line.ticketName || line.priceText)
        .map((line) => ({
            label:
                line.ticketDetail && !line.ticketDetailStrikeThrough
                    ? `${line.ticketName} ${line.ticketDetail}`
                    : line.ticketName,
            originalPrice: line.ticketDetailStrikeThrough ? (line.ticketDetail ?? null) : null,
            price: line.priceText,
        }));

    return {
        name: item.displayTitle,
        dateInfo: item.dateInformation ?? null,
        tickets,
    };
}

type ValueCardReceiptInput = {
    bookingRef: string | null;
    productTitle: string; // e.g. "EXPLORIUM - Medlemskap X"
    itemLabel: string; // e.g. "Aktivitetskort (Norsk)"
    itemPrice: number; // valueCard.price / 100
    netPrice: number; // valueCard.netPrice / 100
    vatRate: number; // valueCard.taxRate / 100
    vatAmount: number; // valueCard.taxAmount / 100
    promoDiscount: number;
    giftCardDiscount: number;
    promoCodeLabel: string | null;
    totalToPay: number;
    logoUrl: string | null;
    locale: string;
    currency: Currency;
    t: Translations;
};

export function buildValueCardReceiptData(input: ValueCardReceiptInput): WidgetReceiptData {
    const { locale, currency, t } = input;

    // The group name already holds the product title; leave the item name empty
    // so the receipt doesn't render "TEST AS NORSK - Medlemskap X" twice.
    const items: ReceiptLineItem[] = [
        {
            name: '',
            dateInfo: null,
            tickets: [
                {
                    label: input.itemLabel,
                    originalPrice: null,
                    price: getLocaleNumberFormatTwoDecimals(locale, input.itemPrice),
                },
            ],
        },
    ];

    const vatLines =
        input.vatAmount > 0
            ? [
                  {
                      label: `${t.vat} ${input.vatRate}%`,
                      amount: getLocaleNumberFormatTwoDecimals(locale, input.vatAmount),
                  },
              ]
            : [];

    const hasPromo = input.promoDiscount > 0;
    const hasGiftCard = input.giftCardDiscount > 0;

    return {
        logoUrl: input.logoUrl,
        heading: capitalize(t.summary),
        bookingRef: input.bookingRef,
        bookingRefLabel: capitalize(t.yourOrderReferenceIs),
        itemGroups: [{ groupName: input.productTitle, items }],
        exVatLabel: t.withoutVat,
        exVatAmount: getLocaleNumberFormatTwoDecimals(locale, input.netPrice),
        vatLines,
        promoLabel: hasPromo ? (input.promoCodeLabel ?? capitalize(t.discount)) : null,
        promoAmount: hasPromo
            ? `-${getLocaleNumberFormatNoDecimals(locale, input.promoDiscount)}`
            : null,
        giftCardLabel: hasGiftCard ? capitalize(t.gift_card) : null,
        giftCardAmount: hasGiftCard
            ? `-${getLocaleNumberFormatNoDecimals(locale, input.giftCardDiscount)}`
            : null,
        totalLabel: t.total.toUpperCase(),
        totalAmount: formatCurrency(locale, currency, input.totalToPay, 0, 2),
        footerNote: t.aConfirmationWillBeSentToYourEmailAddressAsSoonAsWeHaveProcessedYourOrder,
    };
}

type GiftCardReceiptInput = {
    bookingRef: string | null;
    giftcardTotal: number;
    giftcardValue: number;
    giftCardLabelText: string;
    expiryDateText: string | null;
    logoUrl: string | null;
    locale: string;
    currency: Currency;
    t: Translations;
};

export function buildGiftCardReceiptData(input: GiftCardReceiptInput): WidgetReceiptData {
    const { giftcardTotal, giftcardValue, locale, currency, t } = input;

    const items: ReceiptLineItem[] = [
        {
            name: input.giftCardLabelText,
            dateInfo: input.expiryDateText,
            tickets: [
                {
                    label: capitalize(t.gift_card),
                    originalPrice: null,
                    price: getLocaleNumberFormatTwoDecimals(locale, giftcardValue),
                },
            ],
        },
    ];

    return {
        logoUrl: input.logoUrl,
        heading: capitalize(t.summary),
        bookingRef: input.bookingRef,
        bookingRefLabel: capitalize(t.your_booking_reference_is),
        itemGroups: [{ groupName: capitalize(t.gift_card), items }],
        exVatLabel: `${capitalize(t.excl)} ${t.vat}`,
        exVatAmount: getLocaleNumberFormatTwoDecimals(locale, giftcardValue),
        // Gift cards have no VAT (mirrors SummaryGiftcardVat.tsx which shows 0%)
        vatLines: [],
        promoLabel: null,
        promoAmount: null,
        giftCardLabel: null,
        giftCardAmount: null,
        totalLabel: t.total.toUpperCase(),
        totalAmount: formatCurrency(locale, currency, giftcardTotal, 0, 2),
        footerNote: capitalize(t.booking_details_in_email),
    };
}
